import { Container, Sprite } from 'pixi.js';
import { applyLayoutState } from 'libs/layouts/populateLayouts/applyLayoutState';
import type { XY } from 'utils/MathUtils';
import MathUtils from 'utils/MathUtils';
import type { TileStackModel } from 'game/boardMVC/models/board/TileStackModel';
import { waitForMs } from 'utils/async-utils';
import type { ConnectedBlockerView } from '../../types';
import type { MeasureTapeBlockerModel, ChainState } from '../../models/blockers/MeasureTapeBlockerModel';
import { MeasureTapeBlockerLayout } from '../../../../../layouts/blockers/MeasureTapeBlockerLayout';
import type { TileStackView } from '../board/TileStackView';
import { soundManager } from '../../../../app/SoundManager';

const enum MeasureTapeState {
  CaseOnly = 'case_only',
  Case = 'case',
  CaseToHook = 'case_to_hook',
  Blade = 'blade',
  BladeToHook = 'blade_to_hook',
  Hook = 'hook',
  HookToCase = 'hook_to_case',
}

type LayoutState = {
  layout: MeasureTapeBlockerLayout;
  state: MeasureTapeState;
};

const BLOCKER_HIT_ANIMATION_DURATION = 350;
const DIAGONAL_BLADE_POSITION_CASE_TO_HOOK = -256;
const DIAGONAL_BLADE_POSITION_CASE_ONLY = -366;
const DIAGONAL_BLADE_POSITION_BLADE_TO_HOOK = -161;
const DIAGONAL_BLADE_SCALE = 1.14;

export class MeasureTapeBlockerView extends Container implements ConnectedBlockerView {
  private readonly layoutMap = new Map<XY, LayoutState>();

  constructor(
    private readonly model: MeasureTapeBlockerModel,
    private readonly stackView: TileStackView,
  ) {
    super();
    this.updateConnections();
  }

  public updateConnections(): void {
    this.clearConnections();

    this.model.postLevelSetupInit();
    this.model.chainStates.forEach((chainState: ChainState) => {
      this.setupLayout(chainState);
    });

    this.updateLayoutStates();
    this.model.onRetract.connect(this.onRetract.bind(this));
  }

  public async onRetract(direction: XY): Promise<void> {
    if (!direction) return;

    const hitLayout = this.layoutMap.get(direction);

    if (!hitLayout) {
      console.error('MeasureTapeBlockerView: layout not found');
      return;
    }

    await this.animateHit(hitLayout);
    await this.onModelChanged();
  }

  public async onModelChanged(): Promise<void> {
    this.updateLayoutStates();
  }

  public async animateRemoval(): Promise<void> {
    const allChainsEmpty = this.model.chainStates.every((chainState) => chainState.tileStacks.length === 0);

    if (!allChainsEmpty) {
      await waitForMs(BLOCKER_HIT_ANIMATION_DURATION);
    } else {
      // scale layout up to 1.2 and then to 0
      await Promise.all(
        [...this.layoutMap.values()].map(async (layoutState: LayoutState) => {
          if (!layoutState.layout) return;
          await layoutState.layout.root
            .tweenTo({ scaleX: 1.1, scaleY: 1.1 }, BLOCKER_HIT_ANIMATION_DURATION / 2, 'expOut', true)
            .asyncOnComplete();

          soundManager.play('Gameplay_Measure_Tape_Remove');

          await layoutState.layout.root
            .tweenTo({ scaleX: 0, scaleY: 0 }, BLOCKER_HIT_ANIMATION_DURATION / 2, 'expIn', true)
            .asyncOnComplete();
        }),
      );
    }

    this.destroy({ children: true });
  }

  private clearConnections(): void {
    this.model.onRetract.disconnectAll();
    [...this.layoutMap.values()].forEach((layoutState: LayoutState) => {
      layoutState.layout?.root.destroy({ children: true });
    });

    this.layoutMap.clear();
  }

  private setupLayout(chainState: ChainState): void {
    const layout = new MeasureTapeBlockerLayout();

    layout.root.props({
      parent: this,
    });

    this.setRotation(layout, chainState.direction);
    this.layoutMap.set(chainState.direction, { layout, state: MeasureTapeState.CaseOnly });
  }

  private setRotation(layout: MeasureTapeBlockerLayout, direction: XY): void {
    // if the measure tape is case only and therefore has no direction
    // set the direction to down
    const adjustedDirection = MathUtils.comparePoints(direction, { x: 0, y: 0 }) ? { x: 0, y: 1 } : direction;

    let angle = MathUtils.getVectorAngle(adjustedDirection.x, adjustedDirection.y) - Math.PI / 2;

    angle = angle < 0 ? angle + Math.PI * 2 : angle;

    // set the angle -90 deg to the rotation
    layout.measureTape.rotation = angle;
    layout.innerBladeMask.rotation = angle;
  }

  private async animateHit(layoutState: LayoutState): Promise<void> {
    const { layout, state } = layoutState;

    if (state === MeasureTapeState.CaseOnly) return;

    const part1Duration = (BLOCKER_HIT_ANIMATION_DURATION / 7) * 3;
    const part2Duration = (BLOCKER_HIT_ANIMATION_DURATION / 7) * 4;
    const delayDuration = BLOCKER_HIT_ANIMATION_DURATION / 7;

    soundManager.play('Gameplay_Measure_Tape_Retract');
    layout.case.tweenTo({ scaleX: 1.1 }, part1Duration, 'circularIn', true, 0, 0, true);
    layout.stopShadow.tweenTo({ scaleX: 1.1 }, part1Duration, 'circularIn', true, 0, 0, true);

    // thumb lock animation
    void layout.thumb_lock
      .tweenTo({ pivotY: 20 }, part1Duration, 'linear', true)
      .asyncOnComplete()
      .then(() => {
        layout.thumb_lock.tweenTo({ pivotY: 0 }, part2Duration, 'linear', true, part1Duration);
      });

    // retract animation
    if (state === MeasureTapeState.CaseToHook || state === MeasureTapeState.HookToCase) {
      layout.stopShadow.tweenTo({ alpha: 0 }, part2Duration, 'linear', true);
      await layout.blade
        .tweenTo({ pivotY: this.isVertical(layout) ? 80 : 93 }, part2Duration, 'linear', true, delayDuration)
        .asyncOnComplete();
      await waitForMs(delayDuration * 2);
    } else {
      await layout.blade
        .tweenTo({ pivotY: this.isVertical(layout) ? 172 : 200 }, delayDuration * 6, 'backOut', true, delayDuration)
        .asyncOnComplete();
    }
  }

  private updateLayoutStates(): void {
    if (this.destroyed) return;
    this.model.chainStates.forEach((chainState) => {
      const layoutState = this.layoutMap.get(chainState.direction);

      if (!layoutState) {
        console.error('MeasureTapeBlockerView: layout not found');
        return;
      }

      const { layout } = layoutState;
      if (layout.root.destroyed) return;

      const state = this.findMeasureTapeState(chainState);
      layoutState.state = state;
      applyLayoutState(layout, state);

      const isVertical = this.isVertical(layout);
      this.resetBlade(layout, isVertical);

      switch (state) {
        case MeasureTapeState.CaseToHook:
          if (!isVertical) this.updateBladePosition(layout, DIAGONAL_BLADE_POSITION_CASE_TO_HOOK);
          this.applyCaseState(layout);
          break;
        case MeasureTapeState.CaseOnly:
          if (!isVertical) this.updateBladePosition(layout, DIAGONAL_BLADE_POSITION_CASE_ONLY);
          this.applyCaseState(layout);
          break;
        case MeasureTapeState.Case:
          this.applyCaseState(layout);
          break;
        case MeasureTapeState.BladeToHook:
          if (!isVertical) this.updateBladePosition(layout, DIAGONAL_BLADE_POSITION_BLADE_TO_HOOK);
          this.applyBladeState(layout);
          break;
        case MeasureTapeState.Blade:
          this.applyBladeState(layout);
          break;
        case MeasureTapeState.Hook:
        case MeasureTapeState.HookToCase:
          this.applyHookState(layout);
          break;
        default:
          console.error('MeasureTapeBlockerView: state not found for', this.model.id);
          break;
      }
    });
  }

  private findMeasureTapeState(chainState: ChainState): MeasureTapeState {
    let state = MeasureTapeState.CaseOnly;

    const index = chainState.tileStacks.findIndex((stack) => stack === this.stackView.model);

    if (index === -1) {
      return state;
    }

    const { predecessors, successors } = this.findNeighborStacks(chainState);

    // case
    if (this.model.isCase) {
      if (successors.length === 1) {
        state = MeasureTapeState.CaseToHook;
      } else if (successors.length > 0) {
        state = MeasureTapeState.Case;
      } else if (successors.length === 0) {
        state = MeasureTapeState.CaseOnly;
      }
      // hook
    } else if (successors.length === 0) {
      if (predecessors.length === 1) {
        state = MeasureTapeState.HookToCase;
      } else if (predecessors.length > 1) {
        state = MeasureTapeState.Hook;
      }
      //blade
    } else if (successors.length > 0 && predecessors.length > 0) {
      if (successors.length === 1) {
        state = MeasureTapeState.BladeToHook;
      } else if (successors.length > 1) {
        state = MeasureTapeState.Blade;
      }
    } else {
      console.error(
        `MeasureTapeBlockerView: state not found for ${this.model.id}, isCase: ${this.model.isCase}, successors: ${successors.length}, predecessors: ${predecessors.length}`,
      );
    }
    return state;
  }

  private findNeighborStacks(chainState: ChainState): { predecessors: TileStackModel[]; successors: TileStackModel[] } {
    const index = chainState.tileStacks.findIndex((stack) => stack === this.stackView.model);

    if (index === -1) {
      return { predecessors: [], successors: [] };
    }

    // all stacks after the current stack are successors
    const successors = chainState.tileStacks.slice(index + 1);

    // all stacks before the current stack in reverse order are predecessors
    const predecessors = chainState.tileStacks.slice(0, index).reverse();

    return {
      predecessors,
      successors,
    };
  }

  private resetBlade(layout: MeasureTapeBlockerLayout, isVertical: boolean): void {
    if (layout.blade.destroyed) return;
    layout.blade.pivot = { x: 0, y: 0 };
    layout.blade.scaleY = isVertical ? 1 : DIAGONAL_BLADE_SCALE;
  }

  private updateBladePosition(layout: MeasureTapeBlockerLayout, position: number): void {
    layout.blade.position.y = position;
  }

  private applyCaseState(layout: MeasureTapeBlockerLayout): void {
    layout.innerBladeMask.removeFromParent();
    layout.bladeMask.removeFromParent();

    const { rotation } = layout.measureTape;
    layout.innerBladeMask.rotation = rotation;

    const combinedMask = new Sprite({
      texture: this.pixi.renderer.generateTexture(
        new Container({
          children: [layout.bladeMask, layout.innerBladeMask],
        }),
      ),
      label: 'combinedMask',
      parent: layout.root,
      anchor: 0.5,
    });

    const maskSizeDifference = {
      x: combinedMask.width - layout.bladeMask.width,
      y: combinedMask.height - layout.bladeMask.height,
    };

    combinedMask.position.set(
      ((rotation > Math.PI ? -1 : 1) * maskSizeDifference.x) / 2,
      ((rotation === 0 ? -1 : 1) * maskSizeDifference.y) / 2,
    );

    layout.blade.mask = combinedMask;
    layout.blade.anchor.y = this.isVertical(layout) ? 0 : 0.045;
  }

  private applyBladeState(layout: MeasureTapeBlockerLayout): void {
    layout.innerBladeMask.visible = false;
    layout.blade.mask = layout.bladeMask;
    layout.blade.anchor.y = this.isVertical(layout) ? 0.04 : 0.035;
  }

  private applyHookState(layout: MeasureTapeBlockerLayout): void {
    layout.innerBladeMask.visible = false;
    layout.blade.mask = layout.bladeMask;
    layout.blade.anchor.y = this.isVertical(layout) ? 0.05 : 0.13;
  }

  private isVertical(layout: MeasureTapeBlockerLayout): boolean {
    return (
      Math.abs(Math.abs(layout.measureTape.rotation) - Math.PI) < Number.EPSILON ||
      Math.abs(layout.measureTape.rotation) < Number.EPSILON
    );
  }

  public getModel(): MeasureTapeBlockerModel {
    return this.model;
  }
}
